import {useSearchParams} from "react-router-dom";
import {useCallback, useMemo, useState} from "react";
import {useDebouncedCallback} from "use-debounce";

type FilterRecord = Record<string, any>;

interface UseCustomFiltersProps<TFilter extends FilterRecord> {
    tableName: string;
    defaultFilter: TFilter;
    debounce?: number;
    onFilterChanged?: (value: TFilter) => void;
}

function useCustomFilters<TFilter extends FilterRecord>({
                                                            tableName,
                                                            defaultFilter,
                                                            debounce = 300,
                                                            onFilterChanged,
                                                        }: UseCustomFiltersProps<TFilter>) {
    const [searchParams, setSearchParams] = useSearchParams();

    const initialFilter: TFilter = useMemo(() => {
        const obj: Record<string, any> = { ...defaultFilter };

        Object.keys(defaultFilter).forEach((key) => {
            const paramKey = `${tableName}_${key}`;
            const value = searchParams.get(paramKey);

            if (value !== null) {
                const defaultVal = (defaultFilter as any)[key];
                let parsed: any = value;
                if (typeof defaultVal === "boolean") {
                    parsed = value === "true";
                } else if (typeof defaultVal === "number") {
                    const n = Number(value);
                    parsed = isNaN(n) ? defaultVal : n;
                }
                obj[key] = parsed;
            }
        });

        return obj as TFilter;
    }, [defaultFilter, searchParams, tableName]);

    const [currentCustomFilter, setCurrentCustomFilter] = useState<TFilter>(initialFilter);

    const debouncedUpdate = useDebouncedCallback((updated: TFilter) => {
        const newParams = Object.fromEntries(searchParams.entries());

        Object.keys(updated).forEach((key) => {
            const paramKey = `${tableName}_${key}`;

            if (updated[key] === undefined || updated[key] === null || updated[key] === "") {
                delete newParams[paramKey];
            } else {
                newParams[paramKey] = updated[key];
            }
        });

        setSearchParams(newParams, { replace: true });

        if (onFilterChanged) onFilterChanged(updated);
    }, debounce);

    const setCustomField = useCallback(
        (field: keyof TFilter, value: any) => {
            const updated = { ...currentCustomFilter, [field]: value };
            setCurrentCustomFilter(updated);
            debouncedUpdate(updated);
        },
        [currentCustomFilter, debouncedUpdate]
    );

    const setCustomFilter = useCallback(
        (newFilter: TFilter) => {
            setCurrentCustomFilter(newFilter);
            debouncedUpdate(newFilter);
        },
        [debouncedUpdate]
    );

    return {
        currentCustomFilter,
        setCustomField,
        setCustomFilter,
    }
}

export default useCustomFilters;